import { ReactElement, SyntheticEvent, useContext, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import {
  closeOutline,
  personOutline,
  logOutOutline,
  toggleOutline,
  shareOutline,
  bugOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import Icon from "../../atoms/Icon";
import AuthContext from "../../../contexts/AuthContext";
import { useHistory, useParams } from "react-router";
import MenuItem from "./MenuItem";
import LanguageSelectorModal from "../LanguageSelector/LanguageSelectorModal/LanguageSelectorModal";
import ReactCountryFlag from "react-country-flag";
import { langToCountryCode } from "../../../helper/lang";
import { resetCaches } from "../../../services/questionnaire.service";
import IslandSettings from "../../../pages/MentalPlusPages/IslandSettings";
import { settingsOutline } from "ionicons/icons";
import Profile from "../../../pages/Profile/Profile";
import ReportBugModal from "../../../pages/Profile/ReportBugModal";
import { useConfig } from "../../../contexts/ConfigContext";
import { getInvitations, getInvitPubLink } from "../../../services/invitation.service";
import { InvitationChannelEnum, InvitationConfigType } from "../../../types/invitation.types";

interface MenuProps {
  isConnected: boolean;
  onClose(): void;
}

const Menu = ({ isConnected, onClose }: MenuProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study_slug } = useParams<{ study_slug: string }>();

  const { user, endSession } = useContext(AuthContext);
  const history = useHistory();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const stopPropagation = (e: SyntheticEvent) => {
    e.stopPropagation();
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [reportBugOpen, setReportBugOpen] = useState(false);
  const { study } = useConfig();

  const ionRouter = useIonRouter();
  const logOut = async () => {
    await endSession();
    resetCaches(); // Add this line
    // history.replace(`/${study_slug}/welcome`, []);
    ionRouter.push(`/${study_slug}/welcome`, "root", "replace", {
      unmount: true,
    });
  };

  const handleShare = async () => {
  if (!study) return;

  try {
    // 1) Fetch invitation configs for the current user/role
    const { data: invitationConfigs } = await getInvitations({
      studyId: study.id,
      lang: language,
    });

    // 2) Find the config that uses PUBLIC_LINK
    const publicLinkConfig = (invitationConfigs as InvitationConfigType[]).find(
      (config) => config.channel === InvitationChannelEnum.PUBLICLINK
    );

    if (!publicLinkConfig) {
      alert("Aucun lien d’invitation public n’est configuré.");
      return;
    }

    // 3) Ask backend for the public link (with token) for that config
    const { data: link } = await getInvitPubLink(
      { studyId: study.id },
      { invitation_config_id: publicLinkConfig.id }
    );

    const shareUrl = link as string; // backend already returns full URL

    // 4) Share or copy
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Mental+",
          text: "Découvre Mental+ 🙂",
          url: shareUrl,
        });
      } catch (err) {
        console.error("Share aborted or failed:", err);
      }
    } else {
      try {
        await navigator.clipboard?.writeText(shareUrl);
        alert("Lien d’invitation Mental+ copié dans le presse-papiers ✅");
      } catch (err) {
        alert("Le partage n’est pas supporté sur cet appareil.");
        console.error("Clipboard share failed:", err);
      }
    }
  } catch (e) {
    console.error("Error while generating invitation token:", e);
    alert("Impossible de générer un lien d’invitation pour le moment.");
  }
};



  return (
    <Modal onClick={onClose}>
      <MenuList onClick={stopPropagation}>
        <Header>
          <Icon icon={closeOutline} size="large" onClick={onClose} />
        </Header>
        <div>
          {isConnected && (
            <MenuItem
              icon={<IonIcon icon={personOutline} size="large" />}
              title="Profile"
              action={t("common_open")}
              onClick={() => setProfileOpen(true)}
            />
          )}
          {isConnected && (
            <MenuItem
              icon={<IonIcon icon={settingsOutline} size="large" />}
              title="Island Settings"
              action={t("common_open")}
              onClick={() => setSettingsOpen(true)}
            />
          )}
          <MenuItem
            icon={
              <ReactCountryFlag
                countryCode={
                  langToCountryCode[language] || language.toUpperCase()
                }
                svg
                style={{
                  width: "1.5em",
                  height: "1.5em",
                  borderRadius: "3px",
                  objectFit: "cover",
                }}
                title={language}
              />
            }
            title={t(`lang_${language}`)}
            action={t("menu_language_button_text")}
            onClick={(): void => setModalVisible(true)}
          />
          <MenuItem
            icon={<IonIcon icon={shareOutline} size="large" />}
            title="Partager Mental+"
            action="Partager"
            onClick={handleShare}
          />
          {isConnected && (
            <MenuItem
              icon={<IonIcon icon={bugOutline} size="large" />}
              title={t("profile_report_bug")}
              action={t("common_open")}
              onClick={() => setReportBugOpen(true)}
            />
          )}
          {isConnected && (
            <MenuItem
              icon={<IonIcon icon={logOutOutline} size="large" />}
              title={user?.attributes?.firstname || "Logout"}
              action={t("menu_logOut")}
              onClick={() => logOut().then().catch()}
            />
          )}
          <LanguageSelectorModal
            modalVisible={modalVisible}
            onCloseClick={(): void => setModalVisible(false)}
          />
          {/* Island settings modal */}
          <IonModal
            className="resonance-sheetmodal"
            isOpen={settingsOpen}
            backdropDismiss={true}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
            canDismiss={true}
            onDidDismiss={() => setSettingsOpen(false)}
          >
            <IonToolbar>
              <IonTitle>Island Setting</IonTitle>
              <CloseButton slot="end" onClick={() => setSettingsOpen(false)}>
                ×
              </CloseButton>
            </IonToolbar>

            <IslandSettings />
          </IonModal>

          <IonModal
            className="resonance-sheetmodal"
            isOpen={profileOpen}
            backdropDismiss={true}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
            canDismiss={true}
            onDidDismiss={() => setProfileOpen(false)}
          >
            <IonToolbar>
              <IonTitle>Profile</IonTitle>
              <CloseButton slot="end" onClick={() => setProfileOpen(false)}>
                ×
              </CloseButton>
            </IonToolbar>

            <Profile />
          </IonModal>
          <ReportBugModal
            isOpen={reportBugOpen}
            onDismiss={() => setReportBugOpen(false)}
            user={user}
            study={study} // or pass the study object if you have it here
          />
        </div>
      </MenuList>
    </Modal>
  );
};

const Modal = styled.div`
  position: absolute;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: flex-end;
  z-index: 99999999;
`;

const slideAnimation = keyframes`
  from{
    margin-right: -70%;
  }
  to{
    margin-right: 0;
  }
`;
const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const MenuList = styled.div`
  width: 70%;
  max-width: 400px;
  animation: ${slideAnimation} 0.1s ease;
  background-color: white;
`;

const Header = styled.div`
  height: 70px;
  background-color: ${(props) => props.theme.primary.background};
  color: ${(props) => props.theme.primary.contrast};
  display: flex;
  align-items: center;
`;

export default Menu;
